import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Box, Typography, IconButton } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { Rnd } from "react-rnd";
import { useTheme } from "@mui/material";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_IMAGE_ALT,
  MODAL_IMAGE_EDIT_IMAGE_NOT_FOUND,
  MODAL_IMAGE_EDIT_START_CREATING,
  MODAL_IMAGE_EDIT_LAYERS_ANALYZING,
} from "../../../../i18n/keysTranslations";
import ImageActions from "../single/ImageActions";
import ScannerOverlay from "./ScannerOverlay";
import {
  normalizedToPixels,
  pixelsToNormalized,
} from "./layerGeometry";

const CORNER_RESIZE_DIRECTIONS = new Set([
  "topLeft",
  "topRight",
  "bottomLeft",
  "bottomRight",
]);

const MIN_LAYER_SIZE_PX = 8;

/**
 * Converts a viewport pointer into the artboard's layout pixels.
 * Uses the visual rect so CSS zoom / parent transforms stay 1:1 with the cursor.
 */
const clientToCanvasPixels = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return { x: 0, y: 0 };
  }
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.offsetWidth,
    y: ((event.clientY - rect.top) / rect.height) * canvas.offsetHeight,
  };
};

const boxFromElementRect = (element, canvas) => {
  const canvasRect = canvas.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (!canvasRect.width || !canvasRect.height) {
    return {
      x: 0,
      y: 0,
      width: element.offsetWidth,
      height: element.offsetHeight,
    };
  }
  return {
    x:
      ((elementRect.left - canvasRect.left) / canvasRect.width) *
      canvas.offsetWidth,
    y:
      ((elementRect.top - canvasRect.top) / canvasRect.height) *
      canvas.offsetHeight,
    width: (elementRect.width / canvasRect.width) * canvas.offsetWidth,
    height: (elementRect.height / canvasRect.height) * canvas.offsetHeight,
  };
};

const clampBoxToCanvas = (box, canvas) => {
  const canvasWidth = canvas?.offsetWidth || 0;
  const canvasHeight = canvas?.offsetHeight || 0;
  const width = Math.min(
    Math.max(box.width, MIN_LAYER_SIZE_PX),
    canvasWidth || box.width
  );
  const height = Math.min(
    Math.max(box.height, MIN_LAYER_SIZE_PX),
    canvasHeight || box.height
  );
  const maxX = Math.max(canvasWidth - width, 0);
  const maxY = Math.max(canvasHeight - height, 0);
  return {
    x: Math.min(Math.max(box.x, 0), maxX),
    y: Math.min(Math.max(box.y, 0), maxY),
    width,
    height,
  };
};

const isPointInBox = (point, box) =>
  point.x >= box.x &&
  point.x <= box.x + box.width &&
  point.y >= box.y &&
  point.y <= box.y + box.height;

const hitTestEditableLayer = ({
  layers,
  point,
  displayWidth,
  displayHeight,
  selectedLayerId,
}) => {
  const withBoxes = layers
    .filter((layer) => !layer.isBackground)
    .map((layer) => ({
      layer,
      box: normalizedToPixels({
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
        displayWidth,
        displayHeight,
      }),
    }));

  const selectedHit = withBoxes.find(
    ({ layer, box }) =>
      layer.id === selectedLayerId && isPointInBox(point, box)
  );
  if (selectedHit) return selectedHit.layer;

  return (
    withBoxes
      .filter(({ box }) => isPointInBox(point, box))
      .sort(
        (left, right) =>
          (Number(right.layer.zIndex) || 0) - (Number(left.layer.zIndex) || 0)
      )[0]?.layer || null
  );
};

const RESIZE_HANDLE_POINTER_STYLES = {
  top: { pointerEvents: "auto" },
  right: { pointerEvents: "auto" },
  bottom: { pointerEvents: "auto" },
  left: { pointerEvents: "auto" },
  topLeft: { pointerEvents: "auto" },
  topRight: { pointerEvents: "auto" },
  bottomLeft: { pointerEvents: "auto" },
  bottomRight: { pointerEvents: "auto" },
};

const MovableLayer = ({
  layer,
  pixelBox,
  isSelected,
  alt,
  canvasRef,
  canvasScale,
  onResizeOrDragStop,
}) => {
  const [lockAspectRatio, setLockAspectRatio] = useState(false);
  const [liveBox, setLiveBox] = useState(pixelBox);
  const liveBoxRef = useRef(pixelBox);
  const isResizingRef = useRef(false);

  liveBoxRef.current = liveBox;

  useLayoutEffect(() => {
    if (isResizingRef.current) return;
    const nextBox = {
      x: pixelBox.x,
      y: pixelBox.y,
      width: pixelBox.width,
      height: pixelBox.height,
    };
    liveBoxRef.current = nextBox;
    setLiveBox(nextBox);
  }, [pixelBox.x, pixelBox.y, pixelBox.width, pixelBox.height]);

  const commitBox = (nextBox) => {
    liveBoxRef.current = nextBox;
    setLiveBox(nextBox);
    onResizeOrDragStop(layer, nextBox);
  };

  return (
    <Rnd
      bounds="parent"
      disableDragging
      enableResizing={isSelected}
      scale={canvasScale}
      lockAspectRatio={lockAspectRatio}
      size={{
        width: Math.max(liveBox.width, MIN_LAYER_SIZE_PX),
        height: Math.max(liveBox.height, MIN_LAYER_SIZE_PX),
      }}
      position={{ x: liveBox.x, y: liveBox.y }}
      style={{
        zIndex: Number(layer.zIndex) || 0,
        border: isSelected ? "1.5px solid #111" : "1px solid transparent",
        pointerEvents: "none",
      }}
      resizeHandleStyles={isSelected ? RESIZE_HANDLE_POINTER_STYLES : undefined}
      onResizeStart={(_event, direction) => {
        isResizingRef.current = true;
        flushSync(() => {
          setLockAspectRatio(CORNER_RESIZE_DIRECTIONS.has(direction));
        });
      }}
      onResize={(_event, _direction, elementRef) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const nextBox = clampBoxToCanvas(
          boxFromElementRect(elementRef, canvas),
          canvas
        );
        liveBoxRef.current = nextBox;
        setLiveBox(nextBox);
      }}
      onResizeStop={(_event, _direction, elementRef) => {
        isResizingRef.current = false;
        const canvas = canvasRef.current;
        if (!canvas) {
          onResizeOrDragStop(layer, liveBoxRef.current);
          return;
        }
        commitBox(
          clampBoxToCanvas(boxFromElementRect(elementRef, canvas), canvas)
        );
      }}
    >
      <Box
        component="img"
        src={layer.url}
        alt={alt}
        draggable={false}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "fill",
          display: "block",
          pointerEvents: "none",
          userSelect: "none",
        }}
      />
    </Rnd>
  );
};

const LayerCanvas = ({
  imageUrl,
  initialImageUrl,
  layers = [],
  canvasWidth,
  canvasHeight,
  isAnalyzing = false,
  selectedLayerId,
  onSelectLayer,
  onLayerBoxChange,
  isSidebarOpen,
  onToggleSidebar,
  onClose,
  onCallbackSave = () => {},
  showSaveAlert = true,
  saveDisabled = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslationApp();
  const containerRef = useRef(null);
  const artboardRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [canvasScale, setCanvasScale] = useState(1);
  const [activeDrag, setActiveDrag] = useState(null);
  const activeDragRef = useRef(null);
  activeDragRef.current = activeDrag;
  const hasLayers = layers.length > 0;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !canvasWidth || !canvasHeight) return undefined;

    const getContentSize = (entry) => {
      const box = entry?.contentBoxSize?.[0];
      if (box) {
        return {
          width: box.inlineSize,
          height: box.blockSize,
        };
      }
      if (entry?.contentRect) {
        return {
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        };
      }
      const styles = getComputedStyle(element);
      const padX =
        parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const padY =
        parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      return {
        width: element.clientWidth - padX,
        height: element.clientHeight - padY,
      };
    };

    const updateSize = (entry) => {
      const content = getContentSize(entry);
      if (!content.width || !content.height) return;
      const scale = Math.min(
        content.width / canvasWidth,
        content.height / canvasHeight
      );
      const nextWidth = canvasWidth * scale;
      const nextHeight = canvasHeight * scale;
      setDisplaySize((current) => {
        if (
          Math.abs(current.width - nextWidth) < 0.5 &&
          Math.abs(current.height - nextHeight) < 0.5
        ) {
          return current;
        }
        return { width: nextWidth, height: nextHeight };
      });
    };

    updateSize();
    const observer = new ResizeObserver((entries) => {
      updateSize(entries[0]);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [canvasHeight, canvasWidth, hasLayers]);

  useLayoutEffect(() => {
    const canvas = artboardRef.current;
    if (!canvas?.offsetWidth) return;
    const nextScale =
      canvas.getBoundingClientRect().width / canvas.offsetWidth;
    if (!Number.isFinite(nextScale) || nextScale <= 0) return;
    setCanvasScale((current) =>
      Math.abs(current - nextScale) < 0.001 ? current : nextScale
    );
  }, [displaySize.height, displaySize.width, hasLayers]);

  const onResizeOrDragStop = (layer, nextBox) => {
    if (!displaySize.width || !displaySize.height) return;
    onLayerBoxChange(
      layer.id,
      pixelsToNormalized({
        ...nextBox,
        displayWidth: displaySize.width,
        displayHeight: displaySize.height,
      })
    );
  };

  const onArtboardPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (isAnalyzing) return;
    if (event.target.closest?.("[class*='react-resizable-handle']")) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width || !displaySize.height) return;

    const point = clientToCanvasPixels(event, canvas);
    const hitLayer = hitTestEditableLayer({
      layers,
      point,
      displayWidth: displaySize.width,
      displayHeight: displaySize.height,
      selectedLayerId,
    });
    if (!hitLayer) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    onSelectLayer(hitLayer.id);

    const box =
      activeDrag?.layerId === hitLayer.id
        ? activeDrag.box
        : normalizedToPixels({
            x: hitLayer.x,
            y: hitLayer.y,
            width: hitLayer.width,
            height: hitLayer.height,
            displayWidth: displaySize.width,
            displayHeight: displaySize.height,
          });
    const nextDrag = {
      layerId: hitLayer.id,
      box,
      grabOffset: {
        x: point.x - box.x,
        y: point.y - box.y,
      },
    };
    activeDragRef.current = nextDrag;
    setActiveDrag(nextDrag);
  };

  const onArtboardPointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    const canvas = artboardRef.current;
    if (!canvas) return;
    const point = clientToCanvasPixels(event, canvas);
    setActiveDrag((current) => {
      if (!current) return current;
      const next = {
        ...current,
        box: clampBoxToCanvas(
          {
            ...current.box,
            x: point.x - current.grabOffset.x,
            y: point.y - current.grabOffset.y,
          },
          canvas
        ),
      };
      activeDragRef.current = next;
      return next;
    });
  };

  const onArtboardPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const current = activeDragRef.current;
    if (!current) return;
    activeDragRef.current = null;
    setActiveDrag(null);
    const layer = layers.find((item) => item.id === current.layerId);
    if (layer) {
      onResizeOrDragStop(layer, current.box);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100vh",
        maxHeight: "100vh",
      }}
    >
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 4 },
          pt: { xs: 6, sm: 6 },
          pb: { xs: 1, sm: 2 },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)"
          } 0%, ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)"
          } 100%)`,
        }}
      >
        {!isSidebarOpen ? (
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              position: "absolute",
              top: 15,
              left: 10,
              zIndex: 10,
            }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 15,
            right: 6,
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        {imageUrl ? (
          <Box
            ref={containerRef}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "100%",
              maxHeight: "100%",
              overflow: "hidden",
              padding: 2,
            }}
          >
            {hasLayers && displaySize.width > 0 ? (
              <Box
                ref={artboardRef}
                onPointerDown={onArtboardPointerDown}
                onPointerMove={onArtboardPointerMove}
                onPointerUp={onArtboardPointerUp}
                onPointerCancel={onArtboardPointerUp}
                sx={{
                  position: "relative",
                  width: displaySize.width,
                  height: displaySize.height,
                  flexShrink: 0,
                  boxShadow: 3,
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#fff",
                  touchAction: "none",
                }}
              >
                {[...layers]
                  .sort(
                    (left, right) =>
                      (Number(left.zIndex) || 0) - (Number(right.zIndex) || 0)
                  )
                  .map((layer) => {
                    const pixelBox =
                      activeDrag?.layerId === layer.id
                        ? activeDrag.box
                        : normalizedToPixels({
                            x: layer.x,
                            y: layer.y,
                            width: layer.width,
                            height: layer.height,
                            displayWidth: displaySize.width,
                            displayHeight: displaySize.height,
                          });
                    const isSelected = selectedLayerId === layer.id;

                    if (layer.isBackground) {
                      return (
                        <Box
                          key={layer.id}
                          component="img"
                          src={layer.url}
                          alt={layer.name || t(MODAL_IMAGE_EDIT_IMAGE_ALT)}
                          onClick={() => onSelectLayer(layer.id)}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "fill",
                            zIndex: Number(layer.zIndex) || 0,
                            pointerEvents: "none",
                          }}
                        />
                      );
                    }

                    return (
                      <MovableLayer
                        key={layer.id}
                        layer={layer}
                        pixelBox={pixelBox}
                        isSelected={isSelected}
                        alt={layer.name || t(MODAL_IMAGE_EDIT_IMAGE_ALT)}
                        canvasRef={artboardRef}
                        canvasScale={canvasScale}
                        onResizeOrDragStop={onResizeOrDragStop}
                      />
                    );
                  })}
                <ScannerOverlay visible={isAnalyzing} />
              </Box>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt={t(MODAL_IMAGE_EDIT_IMAGE_ALT)}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: 2,
                    boxShadow: 3,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    display: "block",
                  }}
                />
                <ScannerOverlay visible={isAnalyzing} />
                {isAnalyzing ? (
                  <Typography
                    variant="body2"
                    sx={{
                      position: "absolute",
                      left: "50%",
                      bottom: 16,
                      transform: "translateX(-50%)",
                      bgcolor: "rgba(255,255,255,0.9)",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 600,
                      zIndex: 4,
                    }}
                  >
                    {t(MODAL_IMAGE_EDIT_LAYERS_ANALYZING)}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
              {t(MODAL_IMAGE_EDIT_IMAGE_NOT_FOUND)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 400 }}
            >
              {t(MODAL_IMAGE_EDIT_START_CREATING)}
            </Typography>
          </Box>
        )}
      </Box>

      <ImageActions
        imageUrl={imageUrl}
        initialImageUrl={initialImageUrl}
        onCallbackSave={onCallbackSave}
        onClose={onClose}
        showSaveAlert={showSaveAlert}
        disabled={saveDisabled}
      />
    </Box>
  );
};

export default LayerCanvas;
